import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { OnboardingLayout } from '../components/onboarding/OnboardingLayout';
import { OnboardingStep } from '../components/onboarding/OnboardingStep';
import { Step1Business } from '../components/onboarding/Step1Business';
import { Step2WhatTheyDo } from '../components/onboarding/Step2WhatTheyDo';
import { Step3Audience } from '../components/onboarding/Step2Audience';
import { Step4ProblemSolution } from '../components/onboarding/Step4ProblemSolution';
import { Step5CoreMessage } from '../components/onboarding/Step5CoreMessage';
import { Step6Tone } from '../components/onboarding/Step5Tone';
import { StepFAQ } from '../components/onboarding/StepFAQ';
import { Step7Website } from '../components/onboarding/Step7Website';
import { Step8ImageStyle } from '../components/onboarding/Step8ImageStyle';
import { Step9ImageElements } from '../components/onboarding/Step9ImageElements';
import { Step10ImageElementsExclude } from '../components/onboarding/Step10ImageElementsExclude';
import { Step11ImageColors } from '../components/onboarding/Step11ImageColors';
import { StepFinal } from '../components/onboarding/StepFinal';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface OnboardingData {
  step1: { business_name: string };
  step2: { what_they_do: string };
  step3: { target_audience: string };
  step4: { problem: string; solution: string };
  step5: { core_message: string };
  step6: { tone: string };
  step7: { faq_topics: string };
  step8: { website_url: string };
  step9: { image_style: string };
  step10: { image_elements_include: string };
  step11: { image_elements_exclude: string };
  step12: { image_color_preferences: string; no_color_preference?: boolean };
  followups: Record<string, string>;
}

const INITIAL_DATA: OnboardingData = {
  step1: { business_name: '' },
  step2: { what_they_do: '' },
  step3: { target_audience: '' },
  step4: { problem: '', solution: '' },
  step5: { core_message: '' },
  step6: { tone: '' },
  step7: { faq_topics: '' },
  step8: { website_url: '' },
  step9: { image_style: '' },
  step10: { image_elements_include: '' },
  step11: { image_elements_exclude: '' },
  step12: { image_color_preferences: '', no_color_preference: false },
  followups: {},
};

const TOTAL_STEPS = 13;

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { user, refreshUser, loading: authLoading } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState<OnboardingData>(INITIAL_DATA);
  const dataRef = useRef<OnboardingData>(INITIAL_DATA);

  const setDataAndRef = (updater: OnboardingData | ((prev: OnboardingData) => OnboardingData)) => {
    const next = typeof updater === 'function' ? updater(dataRef.current) : updater;
    dataRef.current = next;
    setData(next);
  };

  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState<'idle' | 'running' | 'failed'>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<number>(0);
  const rekickedRef = useRef(false);

  // Vlag in localStorage die een refresh overleeft: zo weten we bij herladen dat
  // er een generatie aan de gang is, ook in het korte venster vlak na de klik
  // voordat de server 'running' heeft weggeschreven.
  const genFlagKey = () => `dibbi_onboarding_generating_${user?.id ?? 'anon'}`;
  const setGenFlag = (on: boolean) => {
    try { on ? localStorage.setItem(genFlagKey(), '1') : localStorage.removeItem(genFlagKey()); } catch { /* ignore */ }
  };
  const getGenFlag = () => {
    try { return localStorage.getItem(genFlagKey()) === '1'; } catch { return false; }
  };

  // Draai (opnieuw) zodra de auth klaar is en user bekend is. Met lege deps draaide
  // dit één keer op mount, vaak vóórdat `user` geladen was (volledige paginalaad),
  // waardoor loadProgress meteen return'de en de completed -> /dashboard-redirect
  // nooit afging. Nu hertriggert het wanneer user beschikbaar komt.
  useEffect(() => {
    if (authLoading) return;
    loadProgress();
  }, [authLoading, user?.id]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const loadProgress = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('company_name')
        .eq('id', user.id)
        .maybeSingle();

      if (userError) throw userError;

      const companyName = userData?.company_name || user.company_name || '';

      const { data: progress, error } = await supabase
        .from('user_onboarding_progress')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (progress) {
        if (progress.completed || progress.skipped) {
          navigate('/dashboard', { replace: true });
          return;
        }

        setCurrentStep(progress.current_step || 1);

        let mergedData = INITIAL_DATA;

        if (progress.step_data && Object.keys(progress.step_data).length > 0) {
          mergedData = { ...INITIAL_DATA, ...progress.step_data } as OnboardingData;
          if (!mergedData.followups) mergedData.followups = {};
        }

        if (companyName && !mergedData.step1.business_name) {
          mergedData = {
            ...mergedData,
            step1: { ...mergedData.step1, business_name: companyName },
          };
        }

        setDataAndRef(mergedData);

        const resuming = progress.profile_status === 'running' || getGenFlag();
        if (resuming && !progress.completed && progress.profile_status !== 'failed') {
          setCurrentStep(TOTAL_STEPS);
          setPhase('running');
          startedAtRef.current = Date.now();
          rekickedRef.current = false;
          startPolling();
        } else if (progress.profile_status === 'failed' && !progress.completed) {
          setCurrentStep(TOTAL_STEPS);
          setPhase('failed');
          setGenFlag(false);
        }
      } else if (companyName) {
        setDataAndRef({
          ...INITIAL_DATA,
          step1: { ...INITIAL_DATA.step1, business_name: companyName },
        });
      }
    } catch (err) {
      console.error('Error loading progress:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveProgress = async (step: number, stepData: OnboardingData) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('user_onboarding_progress')
        .upsert({
          user_id: user.id,
          current_step: step,
          step_data: stepData,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;
    } catch (err) {
      console.error('Error saving progress:', err);
    }
  };

  const advanceToNextStep = async (fromStep: number, currentData: OnboardingData) => {
    const nextStep = fromStep + 1;
    setCurrentStep(nextStep);
    await saveProgress(nextStep, currentData);
  };

  const handleNext = async () => {
    setError(null);

    if (!validateCurrentStep()) {
      setError('Vul alle verplichte velden in om door te gaan');
      return;
    }

    if (currentStep >= TOTAL_STEPS) return;

    await advanceToNextStep(currentStep, dataRef.current);
  };

  const handleBack = async () => {
    setError(null);
    if (currentStep > 1) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      await saveProgress(prevStep, dataRef.current);
    }
  };

  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 1:
        return data.step1.business_name.trim().length > 0;
      case 2:
        return data.step2.what_they_do.trim().length > 0;
      case 3:
        return data.step3.target_audience.trim().length > 0;
      case 4:
        return data.step4.problem.trim().length > 0 && data.step4.solution.trim().length > 0;
      case 5:
        return data.step5.core_message.trim().length > 0;
      case 6:
        return data.step6.tone.trim().length > 0;
      case 7:
        return data.step7.faq_topics.trim().length > 0;
      case 8:
      case 9:
      case 10:
      case 11:
      case 12:
      case 13:
        return true;
      default:
        return false;
    }
  };

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      if (!user?.id) return;
      const { data } = await supabase
        .from('user_onboarding_progress')
        .select('completed, profile_status')
        .eq('user_id', user.id)
        .maybeSingle();
      const elapsed = Date.now() - startedAtRef.current;
      setElapsedMs(elapsed);
      if (data?.completed) {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        setGenFlag(false);
        await refreshUser();
        navigate('/dashboard', { replace: true });
      } else if (data?.profile_status === 'failed') {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        setGenFlag(false);
        setPhase('failed');
      } else if (data?.profile_status !== 'running' && elapsed > 10000 && !rekickedRef.current) {
        // Na 10s nog geen 'running' op de server: de oorspronkelijke aftrap is
        // waarschijnlijk afgebroken (bv. refresh vlak na de klik). Eenmalig
        // opnieuw aftrappen; veilig want de oude aftrap is dan dood.
        rekickedRef.current = true;
        void kickoffGeneration();
      }
    }, 3000);
  };

  const kickoffGeneration = async () => {
    if (!user?.id) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-profile`;
      await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ step_data: dataRef.current }),
      });
    } catch (err) {
      console.error('Aftrap-fetch faalde, val terug op polling:', err);
    }
  };

  const handleComplete = async () => {
    if (!user?.id) return;
    setPhase('running');
    startedAtRef.current = Date.now();
    setElapsedMs(0);
    rekickedRef.current = false;
    setGenFlag(true);
    await kickoffGeneration();
    startPolling();
  };

  const handleSkip = async () => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('user_onboarding_progress')
        .update({
          skipped: true,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id);

      if (error) throw error;

      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('Error skipping onboarding:', err);
      navigate('/dashboard', { replace: true });
    }
  };

  if (loading) {
    return (
      <OnboardingLayout>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout>
      {currentStep === 1 && (
        <OnboardingStep
          title="Wat is de naam van je bedrijf?"
          subtitle="Laten we beginnen met de basis"
          onNext={handleNext}
          showBack={false}
          nextDisabled={!validateCurrentStep()}
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
        >
          <Step1Business
            data={data.step1}
            onChange={(step1) => setDataAndRef(prev => ({ ...prev, step1 }))}
          />
        </OnboardingStep>
      )}

      {currentStep === 2 && (
        <OnboardingStep
          title="Wat doet jouw bedrijf precies?"
          subtitle="Beschrijf wat je doet en hoe je het aanpakt"
          onNext={handleNext}
          onBack={handleBack}
          nextDisabled={!validateCurrentStep()}
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
        >
          <Step2WhatTheyDo
            data={data.step2}
            onChange={(step2) => setDataAndRef(prev => ({ ...prev, step2 }))}
          />
        </OnboardingStep>
      )}

      {currentStep === 3 && (
        <OnboardingStep
          title="Wie zijn jouw ideale klanten?"
          subtitle="Hoe specifieker, hoe beter"
          onNext={handleNext}
          onBack={handleBack}
          nextDisabled={!validateCurrentStep()}
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
        >
          <Step3Audience
            data={data.step3}
            onChange={(step3) => setDataAndRef(prev => ({ ...prev, step3 }))}
          />
        </OnboardingStep>
      )}

      {currentStep === 4 && (
        <OnboardingStep
          title="Waarom kiezen mensen voor jou?"
          subtitle="Dit is de kern van je verhaal"
          onNext={handleNext}
          onBack={handleBack}
          nextDisabled={!validateCurrentStep()}
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
        >
          <Step4ProblemSolution
            data={data.step4}
            onChange={(step4) => setDataAndRef(prev => ({ ...prev, step4 }))}
          />
        </OnboardingStep>
      )}

      {currentStep === 5 && (
        <OnboardingStep
          title="Wat is jouw kernboodschap?"
          subtitle="Wat wil je dat mensen onthouden?"
          onNext={handleNext}
          onBack={handleBack}
          nextDisabled={!validateCurrentStep()}
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
        >
          <Step5CoreMessage
            data={data.step5}
            onChange={(step5) => setDataAndRef(prev => ({ ...prev, step5 }))}
          />
        </OnboardingStep>
      )}

      {currentStep === 6 && (
        <OnboardingStep
          title="Hoe wil je overkomen?"
          subtitle="Kies je communicatiestijl"
          onNext={handleNext}
          onBack={handleBack}
          nextDisabled={!validateCurrentStep()}
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
        >
          <Step6Tone
            data={data.step6}
            onChange={(step6) => setDataAndRef(prev => ({ ...prev, step6 }))}
          />
        </OnboardingStep>
      )}

      {currentStep === 7 && (
        <OnboardingStep
          title="Welke vragen krijg jij het vaakst?"
          subtitle="Dit helpt ons relevante blogonderwerpen te bedenken"
          onNext={handleNext}
          onBack={handleBack}
          nextDisabled={!validateCurrentStep()}
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
        >
          <StepFAQ
            data={data.step7}
            onChange={(step7) => setDataAndRef(prev => ({ ...prev, step7 }))}
          />
        </OnboardingStep>
      )}

      {currentStep === 8 && (
        <OnboardingStep
          title="Heb je een website?"
          subtitle="Dit geeft ons extra context (optioneel)"
          onNext={handleNext}
          onBack={handleBack}
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
        >
          <Step7Website
            data={data.step8}
            onChange={(step8) => setDataAndRef(prev => ({ ...prev, step8 }))}
          />
        </OnboardingStep>
      )}

      {currentStep === 9 && (
        <OnboardingStep
          title="Welke afbeeldingsstijl past bij jouw merk?"
          subtitle="Kies je visuele stijl"
          onNext={handleNext}
          onBack={handleBack}
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
        >
          <Step8ImageStyle
            data={data.step9}
            onChange={(step9) => setDataAndRef(prev => ({ ...prev, step9 }))}
          />
        </OnboardingStep>
      )}

      {currentStep === 10 && (
        <OnboardingStep
          title="Wat moet er in beeld komen?"
          subtitle="Welke elementen passen bij jouw bedrijf?"
          onNext={handleNext}
          onBack={handleBack}
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
        >
          <Step9ImageElements
            data={data.step10}
            onChange={(step10) => setDataAndRef(prev => ({ ...prev, step10 }))}
          />
        </OnboardingStep>
      )}

      {currentStep === 11 && (
        <OnboardingStep
          title="Wat moet er NIET in beeld komen?"
          subtitle="Wat wil je juist vermijden?"
          onNext={handleNext}
          onBack={handleBack}
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
        >
          <Step10ImageElementsExclude
            data={data.step11}
            onChange={(step11) => setDataAndRef(prev => ({ ...prev, step11 }))}
          />
        </OnboardingStep>
      )}

      {currentStep === 12 && (
        <OnboardingStep
          title="Welke kleuren passen bij je merk?"
          subtitle="Als je specifieke kleuren wilt (optioneel)"
          onNext={handleNext}
          onBack={handleBack}
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
        >
          <Step11ImageColors
            data={data.step12}
            onChange={(step12) => setDataAndRef(prev => ({ ...prev, step12 }))}
          />
        </OnboardingStep>
      )}

      {currentStep === 13 && (
        <OnboardingStep
          title="Klaar om te beginnen!"
          onBack={handleBack}
          showBack={phase === 'idle'}
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
        >
          <StepFinal
            businessName={data.step1.business_name}
            phase={phase}
            elapsedMs={elapsedMs}
            onComplete={handleComplete}
          />
        </OnboardingStep>
      )}

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
    </OnboardingLayout>
  );
}
